import { Button } from 'components/Button/Button'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import Map, { MapRef, MapboxEvent, NavigationControl } from 'react-map-gl'
import { MAPBOX_ACCESS_TOKEN, SatelliteStyle, STYLES } from 'utils/constants'
import { IMapboxElement } from 'utils/interfaces'
import style from './MapboxCommon.module.css'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
// import MapboxLanguage from '@mapbox/mapbox-gl-language'

import 'mapbox-gl/dist/mapbox-gl.css'

interface MapboxProps {
  onMapMoved?: () => void
  onMapInited?: () => void
  children?: React.ReactNode
}

const MapboxCommon = React.forwardRef<IMapboxElement, MapboxProps>((props, ref) => {
  const { t, i18n } = useTranslation()
  const mapRef = useRef<MapRef>(null)
  const [isSatellite, setIsSatellite] = useState<boolean>(false)

  const mapStyle = useMemo(() => {
    if (!isSatellite) return STYLES.VECTOR
    const sat: SatelliteStyle = STYLES.SATELLITE
    return typeof sat === 'function' ? sat(i18n.language) : sat
  }, [isSatellite, i18n.language])

  const updateMapLanguage = useCallback((language: string) => {
    const map = mapRef?.current?.getMap()
    if (!map) return

    const style = map.getStyle()
    if (!style || !style.layers) return

    let styleChanged = false

    style.layers.forEach((layer: any) => {
      if (layer.type === 'symbol' && layer.layout && layer.layout['text-field']) {
        const textField = layer.layout['text-field']
        
        if (Array.isArray(textField)) {
          const updateExpression = (expr: any): any => {
            if (Array.isArray(expr)) {
              if (expr[0] === 'coalesce') {
                const args = expr.slice(1)
                const updatedArgs = args.map((arg: any) => {
                  if (Array.isArray(arg) && arg[0] === 'get') {
                    const field = arg[1]
                    if (field?.startsWith('name:')) {
                      return ['get', `name:${language}`]
                    }
                  }
                  return arg
                })
                const langField = ['get', `name:${language}`]
                if (!updatedArgs.some((arg: any) => 
                  Array.isArray(arg) && arg[0] === 'get' && arg[1] === `name:${language}`
                )) {
                  updatedArgs.unshift(langField)
                }
                return ['coalesce', ...updatedArgs]
              }
              return expr.map((item: any) => updateExpression(item))
            }
            return expr
          }
          const newTextField = updateExpression(textField)
          if (JSON.stringify(newTextField) !== JSON.stringify(textField)) {
            layer.layout['text-field'] = newTextField
            styleChanged = true
          }
        }
      }
    })

    if (styleChanged) {
      map.setStyle(style)
    }
  }, [])

  const initMap = (e: MapboxEvent<undefined>) => {
    props.onMapInited && props.onMapInited()

    // const map = e.target as MapboxMap
    // const browserLanguage = navigator.language.split('-')[0]
    // const language = new MapboxLanguage({ defaultLanguage: browserLanguage })
    // map.addControl(language)
    // const style = language.setLanguage(map.getStyle(), 'ru')
    // map.setStyle(style)
  }

  useEffect(() => {
    const map = mapRef?.current?.getMap()
    props.onMapMoved && map?.on('moveend', props.onMapMoved)
    return () => {
      props.onMapMoved && map?.off('moveend', props.onMapMoved)
    }
  }, [props.onMapMoved])

  useImperativeHandle(ref, () => ({
    getMap() {
      return mapRef?.current?.getMap()
    },
  }))

  const onMapLoad = useCallback((e: MapboxEvent<undefined>) => {
    initMap(e)
    // Устанавливаем язык после загрузки карты
    updateMapLanguage(i18n.language)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMapLanguage])

  // Обновляем язык при его изменении
  useEffect(() => {
    const map = mapRef?.current?.getMap()
    if (map && map.isStyleLoaded()) {
      updateMapLanguage(i18n.language)
    }
  }, [i18n.language, updateMapLanguage])

  const onSatelliteClicked = () => {
    setIsSatellite((prev) => !prev)
  }

  return (
    <Map
      ref={mapRef}
      onLoad={onMapLoad}
      initialViewState={{
        longitude: 49.14,
        latitude: 55.524,
        zoom: 10,
      }}
      mapStyle={mapStyle}
      mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      attributionControl={false}
      transformRequest={(url, resourceType) => {
        if (resourceType === 'Tile' && url.includes('google.com')) {
          return { url, referrerPolicy: 'no-referrer' }
        }
        return { url }
      }}
    >
      <NavigationControl showCompass={false} position={'bottom-right'} />
      <Button.Standart className={style.satelliteButton} onClick={onSatelliteClicked}>
        <span className={clsx('mapboxgl-ctrl-icon', style.icon)} title={t('map.satellite_vector')} />
      </Button.Standart>
      {props.children}
    </Map>
  )
})

export default MapboxCommon
